import type { FlowNode, MarketFrame, VisualizationConfig } from '../types/market'
import { clamp, hashString } from '../utils/math'
const EMPTY_TRAIL: TrailPoint[] = []
const CHAMPION_EMISSION_INTERVAL = 150

export interface TrailPoint {
  x: number
  y: number
  age: number
}

export interface AxisRipple {
  id: string
  x: number
  startedAt: number
  duration: number
  polarity: number
}

interface NodeEffect {
  reversedAt: number
  reverseDuration: number
  burstAt: number
  burstDuration: number
}

export interface ChampionParticle {
  active: boolean
  sourceId: string
  championId: string
  startedAt: number
  duration: number
  startX: number
  startY: number
  controlX: number
  controlY: number
  endX: number
  endY: number
  size: number
  brightness: number
  colorIndex: number
}

export class EventEffects {
  private transitionKey = ''
  private lastTime = 0
  private readonly nodeEffects = new Map<string, NodeEffect>()
  private readonly trails = new Map<string, TrailPoint[]>()
  private readonly ripples: AxisRipple[] = []
  private readonly liveNodes = new Map<string, FlowNode>()
  private readonly championParticles: ChampionParticle[]
  private championId: string | null = null
  private previousChampionId: string | null = null
  private championChangedAt = 0
  private previousChampionStartOpacity = 0
  private outflowChampionId: string | null = null
  private previousOutflowChampionId: string | null = null
  private outflowChampionChangedAt = 0
  private previousOutflowChampionStartOpacity = 0
  private lastChampionEmissionStep = -1
  private particleCursor = 0

  constructor(private readonly config: VisualizationConfig) {
    this.championParticles = Array.from(
      { length: Math.max(0, Math.floor(config.championParticleMaxCount)) },
      () => ({
        active: false,
        sourceId: '',
        championId: '',
        startedAt: 0,
        duration: 0,
        startX: 0,
        startY: 0,
        controlX: 0,
        controlY: 0,
        endX: 0,
        endY: 0,
        size: 0,
        brightness: 0,
        colorIndex: 0,
      }),
    )
  }

  prepareTransition(
    left: MarketFrame,
    right: MarketFrame,
    rightById: ReadonlyMap<string, MarketFrame['sectors'][number]>,
    leftIndex: number,
    now: number,
  ): void {
    if (now < this.lastTime) this.reset()
    this.lastTime = now
    const key = `${leftIndex}:${left.time}:${right.time}`
    if (key === this.transitionKey) return
    this.transitionKey = key
    if (this.config.championEffectEnabled) this.updateChampion(left, now)
    for (const previous of left.sectors) {
      const next = rightById.get(previous.id)
      if (!next) continue
      const existing = this.nodeEffects.get(previous.id) ?? {
        reversedAt: -Infinity,
        reverseDuration: this.config.marketMinuteDuration,
        burstAt: -Infinity,
        burstDuration: 760,
      }
      const reversed = previous.flow !== 0 && next.flow !== 0 && Math.sign(previous.flow) !== Math.sign(next.flow)
      const delta = Math.abs(next.flow - previous.flow)
      const ratio = delta / Math.max(Math.abs(previous.flow), 100_000_000)
      const burst = delta >= this.config.burstAbsoluteThreshold || ratio >= this.config.burstRatioThreshold

      if (reversed) {
        existing.reversedAt = now
        existing.reverseDuration = this.config.marketMinuteDuration
        this.ripples.push({
          id: `${key}:${previous.id}`,
          x: 0,
          startedAt: now + this.config.marketMinuteDuration * 0.46,
          duration: 520,
          polarity: Math.sign(next.flow),
        })
      }
      if (burst) {
        existing.burstAt = now
        existing.burstDuration = Math.max(620, this.config.marketMinuteDuration * 1.8)
      }
      this.nodeEffects.set(previous.id, existing)
    }
  }

  recordPositions(
    nodes: FlowNode[],
    now: number,
    { allowChampionParticleEmission = true }: { allowChampionParticleEmission?: boolean } = {},
  ): void {
    for (const node of nodes) {
      this.liveNodes.set(node.id, node)
      const intensity = this.reversalIntensity(node.id, now)
      if (intensity > 0 && node.x != null && node.y != null) {
        const points = this.trails.get(node.id) ?? []
        const last = points.at(-1)
        if (!last || Math.hypot(last.x - node.x, last.y - node.y) > 3.5) {
          points.push({ x: node.x, y: node.y, age: now })
          if (points.length > 11) points.shift()
          this.trails.set(node.id, points)
        }
      }
    }
    for (const [id, points] of this.trails) {
      let writeIndex = 0
      for (let index = 0; index < points.length; index += 1) {
        if (now - points[index].age < 430) points[writeIndex++] = points[index]
      }
      points.length = writeIndex
      if (!writeIndex) this.trails.delete(id)
    }
    while (this.ripples.length && now - this.ripples[0].startedAt > this.ripples[0].duration) {
      this.ripples.shift()
    }
    this.updateChampionParticles(nodes, now, allowChampionParticleEmission)
  }

  setRipplePosition(nodeId: string, x: number): void {
    for (let index = this.ripples.length - 1; index >= 0; index -= 1) {
      if (this.ripples[index].id.endsWith(`:${nodeId}`) && this.ripples[index].x === 0) {
        this.ripples[index].x = x
        break
      }
    }
  }

  burstIntensity(id: string, now: number): number {
    const effect = this.nodeEffects.get(id)
    if (!effect) return 0
    const elapsed = now - effect.burstAt
    if (elapsed < 0 || elapsed > effect.burstDuration) return 0
    const progress = elapsed / effect.burstDuration
    return Math.sin(progress * Math.PI) * (1 - progress * 0.25)
  }

  reversalIntensity(id: string, now: number): number {
    const effect = this.nodeEffects.get(id)
    if (!effect) return 0
    const progress = (now - effect.reversedAt) / effect.reverseDuration
    return progress >= 0 && progress <= 1 ? Math.sin(progress * Math.PI) : 0
  }

  getTrail(id: string, now: number): TrailPoint[] {
    void now
    return this.trails.get(id) ?? EMPTY_TRAIL
  }

  getRipples(): readonly AxisRipple[] {
    return this.ripples
  }

  getCurrentChampionId(): string | null {
    return this.championId
  }

  getPreviousChampionId(): string | null {
    return this.previousChampionId
  }

  championOpacity(id: string, now: number): number {
    return this.resolveChampionOpacity(
      id,
      now,
      this.championId,
      this.previousChampionId,
      this.championChangedAt,
      this.previousChampionStartOpacity,
    )
  }

  getCurrentOutflowChampionId(): string | null {
    return this.outflowChampionId
  }

  getPreviousOutflowChampionId(): string | null {
    return this.previousOutflowChampionId
  }

  outflowChampionOpacity(id: string, now: number): number {
    return this.resolveChampionOpacity(
      id,
      now,
      this.outflowChampionId,
      this.previousOutflowChampionId,
      this.outflowChampionChangedAt,
      this.previousOutflowChampionStartOpacity,
    )
  }

  private resolveChampionOpacity(
    id: string,
    now: number,
    currentId: string | null,
    previousId: string | null,
    changedAt: number,
    previousStartOpacity: number,
  ): number {
    const duration = Math.max(180, Math.min(420, this.config.marketMinuteDuration * 1.5))
    const progress = clamp((now - changedAt) / duration, 0, 1)
    const eased = progress * progress * (3 - 2 * progress)
    if (id === currentId) return eased
    if (id === previousId) return previousStartOpacity * (1 - eased)
    return 0
  }

  getChampionParticles(): readonly ChampionParticle[] {
    return this.championParticles
  }

  championAbsorbIntensity(id: string, now: number): number {
    let intensity = 0
    for (const particle of this.championParticles) {
      if (!particle.active || particle.championId !== id) continue
      const progress = (now - particle.startedAt) / particle.duration
      if (progress > 0.8 && progress < 1) intensity += (progress - 0.8) * 1.2
    }
    return clamp(intensity, 0, 1)
  }

  reset(): void {
    this.transitionKey = ''
    this.nodeEffects.clear()
    this.trails.clear()
    this.ripples.length = 0
    this.liveNodes.clear()
    this.championId = null
    this.previousChampionId = null
    this.championChangedAt = 0
    this.previousChampionStartOpacity = 0
    this.outflowChampionId = null
    this.previousOutflowChampionId = null
    this.outflowChampionChangedAt = 0
    this.previousOutflowChampionStartOpacity = 0
    this.lastChampionEmissionStep = -1
    this.particleCursor = 0
    for (const particle of this.championParticles) particle.active = false
  }

  private updateChampion(frame: MarketFrame, now: number): void {
    let nextChampionId: string | null = null
    let nextOutflowChampionId: string | null = null
    let largestInflow = 0
    let smallestOutflow = 0
    for (const sector of frame.sectors) {
      if (
        sector.flow > largestInflow ||
        (sector.flow === largestInflow && sector.flow > 0 && (nextChampionId == null || sector.id < nextChampionId))
      ) {
        largestInflow = sector.flow
        nextChampionId = sector.id
      }
      if (
        sector.flow < smallestOutflow ||
        (sector.flow === smallestOutflow
          && sector.flow < 0
          && (nextOutflowChampionId == null || sector.id < nextOutflowChampionId))
      ) {
        smallestOutflow = sector.flow
        nextOutflowChampionId = sector.id
      }
    }
    if (nextChampionId !== this.championId) {
      const outgoingOpacity = this.championId ? this.championOpacity(this.championId, now) : 0
      this.previousChampionId = this.championId
      this.previousChampionStartOpacity = outgoingOpacity
      this.championId = nextChampionId
      this.championChangedAt = now
    }
    if (nextOutflowChampionId !== this.outflowChampionId) {
      const outgoingOpacity = this.outflowChampionId
        ? this.outflowChampionOpacity(this.outflowChampionId, now)
        : 0
      this.previousOutflowChampionId = this.outflowChampionId
      this.previousOutflowChampionStartOpacity = outgoingOpacity
      this.outflowChampionId = nextOutflowChampionId
      this.outflowChampionChangedAt = now
    }
  }

  private updateChampionParticles(nodes: FlowNode[], now: number, allowEmission: boolean): void {
    for (const particle of this.championParticles) {
      if (particle.active && now - particle.startedAt >= particle.duration) particle.active = false
    }
    if (!allowEmission) {
      this.lastChampionEmissionStep = Math.floor(now / CHAMPION_EMISSION_INTERVAL)
      return
    }
    if (!this.config.championEffectEnabled || !this.config.championParticleEnabled || !this.championId) {
      this.lastChampionEmissionStep = Math.floor(now / CHAMPION_EMISSION_INTERVAL)
      return
    }

    const champion = this.liveNodes.get(this.championId)
    if (!champion || champion.x == null || champion.y == null) return
    const currentStep = Math.floor(now / CHAMPION_EMISSION_INTERVAL)
    if (this.lastChampionEmissionStep < 0) this.lastChampionEmissionStep = currentStep - 1
    const firstStep = Math.max(this.lastChampionEmissionStep + 1, currentStep - 2)
    for (let step = firstStep; step <= currentStep; step += 1) {
      this.emitChampionParticles(nodes, champion, step)
    }
    this.lastChampionEmissionStep = currentStep
  }

  private emitChampionParticles(nodes: FlowNode[], champion: FlowNode, step: number): void {
    let largestInflow = champion.flow
    for (const node of nodes) {
      if (node.flow > largestInflow) largestInflow = node.flow
    }
    if (largestInflow <= 0) return

    // Keep one restrained stream alive for every positive source first. With
    // the configured 60-slot pool this covers all possible sources in the
    // fixed board set before stronger flows receive any extra particles.
    for (const source of nodes) {
      if (source.id === champion.id || source.flow <= 0 || source.x == null || source.y == null) continue
      if (this.activeParticlesForSource(source.id) > 0) continue
      const strength = Math.sqrt(clamp(source.flow / largestInflow, 0, 1))
      const particle = this.acquireParticle()
      if (!particle) return
      this.configureParticle(particle, source, champion, step, strength, 0)
    }

    for (const source of nodes) {
      if (source.id === champion.id || source.flow <= 0 || source.x == null || source.y == null) continue
      const strength = Math.sqrt(clamp(source.flow / largestInflow, 0, 1))
      const maxForSource = 1 + Math.floor(strength * 2)
      if (this.activeParticlesForSource(source.id) >= maxForSource) continue
      const chance = clamp(this.config.championParticleRate * (0.35 + strength * 0.65), 0, 1)
      if (this.seededUnit(`${step}:${source.id}:${champion.id}:extra`) >= chance) continue
      const particle = this.acquireParticle()
      if (!particle) return
      this.configureParticle(particle, source, champion, step, strength, 1)
    }
  }

  private configureParticle(
    particle: ChampionParticle,
    source: FlowNode,
    champion: FlowNode,
    step: number,
    strength: number,
    variant: number,
  ): void {
    const pathSeed = `${step}:${source.id}:${champion.id}:${variant}`
    const sourceX = source.x ?? 0
    const sourceY = source.y ?? 0
    const championX = champion.x ?? 0
    const championY = champion.y ?? 0
    const dx = championX - sourceX
    const dy = championY - sourceY
    const distance = Math.max(1, Math.hypot(dx, dy))
    const unitX = dx / distance
    const unitY = dy / distance
    const perpendicularX = -unitY
    const perpendicularY = unitX
    const bendDirection = this.seededUnit(`${pathSeed}:direction`) < 0.5 ? -1 : 1
    const bend = bendDirection * (24 + Math.min(92, distance * 0.14)) * (0.55 + this.seededUnit(`${pathSeed}:bend`) * 0.6)

    particle.active = true
    particle.sourceId = source.id
    particle.championId = champion.id
    particle.startedAt = step * CHAMPION_EMISSION_INTERVAL
      + this.seededUnit(`${pathSeed}:offset`) * CHAMPION_EMISSION_INTERVAL * 0.82
    particle.duration = (720 + this.seededUnit(`${pathSeed}:duration`) * 360) / Math.max(0.25, this.config.championParticleSpeed)
    particle.startX = sourceX + unitX * (source.radius + 3)
    particle.startY = sourceY + unitY * (source.radius + 3)
    particle.endX = championX - unitX * (champion.radius * 0.8)
    particle.endY = championY - unitY * (champion.radius * 0.8)
    particle.controlX = (particle.startX + particle.endX) * 0.5 + perpendicularX * bend
    particle.controlY = (particle.startY + particle.endY) * 0.5 + perpendicularY * bend
    particle.size = 2 + this.seededUnit(`${pathSeed}:size`) * 2.6
    particle.brightness = 0.46 + strength * 0.34
    particle.colorIndex = Math.floor(this.seededUnit(`${pathSeed}:color`) * 3)
  }

  private acquireParticle(): ChampionParticle | null {
    for (let offset = 0; offset < this.championParticles.length; offset += 1) {
      const index = (this.particleCursor + offset) % this.championParticles.length
      const particle = this.championParticles[index]
      if (particle.active) continue
      this.particleCursor = (index + 1) % this.championParticles.length
      return particle
    }
    return null
  }

  private activeParticlesForSource(sourceId: string): number {
    let count = 0
    for (const particle of this.championParticles) {
      if (particle.active && particle.sourceId === sourceId) count += 1
    }
    return count
  }

  private seededUnit(key: string): number {
    return hashString(key) / 0x1_0000_0000
  }
}
